import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { agentsData } from "Stores/agentsData";
import { userDetail } from "Stores/userDetail";
import AgentSelect from "./AgentSelect";
import DayOfWeekField, { DEFAULT_DAYS, scheduleText } from "./DayOfWeekField";
import SequencePickerModal from "./SequencePickerModal";

const CreateListProspector = observer(() => {
  const history = useHistory();
  const [name, setName] = useState("");
  const [sourceType, setSourceType] = useState("list"); // list | saved_search
  const [listId, setListId] = useState("");
  const [listName, setListName] = useState("");
  const [savedSearchId, setSavedSearchId] = useState("");
  const [savedSearchName, setSavedSearchName] = useState("");
  const [sequenceId, setSequenceId] = useState("");
  const [sequenceName, setSequenceName] = useState("");
  const [seqModalOpen, setSeqModalOpen] = useState(false);
  const [runWhen, setRunWhen] = useState("DAILY"); // DAILY | WEEKLY | MANUAL
  const [time, setTime] = useState("08:00");
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [dailyCap, setDailyCap] = useState(25);
  const [notifyMode, setNotifyMode] = useState("DAILY_DIGEST");
  const [saving, setSaving] = useState(false);
  const timezone = userDetail.timeZone || "UTC";

  useEffect(() => {
    agentsData.fetchContactLists();
    agentsData.fetchSavedSearches();
  }, []);

  const sourcePicked = sourceType === "list" ? !!listId : !!savedSearchId;
  const sourceLabel = sourceType === "list" ? (listName || "a list") : (savedSearchName || "a saved search");
  const canSubmit = sourcePicked && !!sequenceId;

  const buildPayload = () => ({
    name: name.trim() || "Prospect from " + sourceLabel,
    sourceType,
    listId: sourceType === "list" ? listId : undefined,
    savedSearchId: sourceType === "saved_search" ? savedSearchId : undefined,
    sequenceId,
    dailyCap: Number(dailyCap),
    notifyMode,
    timezone,
    ...(runWhen === "MANUAL" ? {} : { frequency: runWhen, time, ...(runWhen === "WEEKLY" ? { days } : {}) })
  });

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const agent = await agentsData.createListProspector(buildPayload());
    setSaving(false);
    if (agent && agent.id) history.push(`/agents/${agent.id}`);
  };

  // Open this template's steps in the canvas to tweak before creating.
  const customize = async () => {
    setSaving(true);
    const draft = await agentsData.composeTemplate("list-prospector", buildPayload());
    setSaving(false);
    if (draft) { agentsData.stageDraft(draft); history.push("/agents/new/custom"); }
  };

  const step = node => (<li><span className="ck">✓</span><span>{node}</span></li>);
  const runText = runWhen === "MANUAL" ? "when you run it" : scheduleText(runWhen, time, days);

  return (
    <div className="ag-page">
      <div className="ag-page__head">
        <div>
          <button className="ag-link" onClick={() => history.push("/agents/new")}>← Back</button>
          <h1 className="ag-page__title">Hire · Prospect from a list</h1>
          <p className="ag-page__sub">Pull contacts from a list or saved search, enrich them, and enroll them into a sequence.</p>
        </div>
      </div>

      <div className="ag-setup">
        <div className="ag-card-panel">
          <div className="ag-fsec">Source</div>
          <label className="ag-field">
            <span className="ag-field__label">Where should contacts come from?<b className="ag-req">required</b></span>
            <AgentSelect
              value={sourceType}
              onChange={setSourceType}
              options={[
                { value: "list", label: "A contact list" },
                { value: "saved_search", label: "A saved List Builder search" }
              ]}
            />
          </label>
          {sourceType === "list" ? (
            <label className="ag-field">
              <span className="ag-field__label">Contact list</span>
              <AgentSelect
                searchable placeholder="Select a contact list…"
                value={listId}
                onChange={v => { const o = (agentsData.contactLists || []).find(x => x.id === v); setListId(v); setListName(o ? o.name : v); }}
                options={(agentsData.contactLists || []).map(l => ({ value: l.id, label: l.count ? `${l.name} · ${l.count}` : l.name }))}
              />
            </label>
          ) : (
            <label className="ag-field">
              <span className="ag-field__label">Saved search</span>
              <AgentSelect
                searchable placeholder="Select a saved search…"
                value={savedSearchId}
                onChange={v => { const o = (agentsData.savedSearches || []).find(x => x.id === v); setSavedSearchId(v); setSavedSearchName(o ? o.label : v); }}
                options={(agentsData.savedSearches || []).map(s => ({ value: s.id, label: s.label }))}
              />
              <span className="ag-hint">Saved searches you built in List Builder. None here? Save one there first.</span>
            </label>
          )}
          <label className="ag-field">
            <span className="ag-field__label">Agent name</span>
            <input className="ag-input" value={name} placeholder={"Prospect from " + sourceLabel} onChange={e => setName(e.target.value)} />
          </label>

          <div className="ag-fsec">What happens next</div>
          <label className="ag-field">
            <span className="ag-field__label">Enroll into sequence<b className="ag-req">required</b></span>
            <button type="button" className="ag-select__btn" onClick={() => setSeqModalOpen(true)}>
              <span className={sequenceName ? "" : "ag-select__ph"}>{sequenceName || "Select a sequence…"}</span>
              <svg className="ag-select__chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
            </button>
            <span className="ag-hint">Contacts are deduped and undeliverables dropped first; email sequences enrich the email before enrolling.</span>
          </label>

          <div className="ag-fsec">Schedule</div>
          <div className="ag-field-row">
            <label className="ag-field"><span className="ag-field__label">Run</span>
              <AgentSelect value={runWhen} onChange={setRunWhen} options={[
                { value: "DAILY", label: "Every day" },
                { value: "WEEKDAYS", label: "Weekdays (Mon–Fri)" },
                { value: "WEEKLY", label: "Specific days" },
                { value: "MANUAL", label: "Only when I run it" }
              ]} />
            </label>
            {runWhen === "WEEKLY" && <DayOfWeekField value={days} onChange={setDays} />}
            {runWhen !== "MANUAL" && (
              <label className="ag-field"><span className="ag-field__label">At</span>
                <input className="ag-input" type="time" value={time} onChange={e => setTime(e.target.value)} />
              </label>
            )}
            <label className="ag-field"><span className="ag-field__label">Contacts / run</span>
              <input className="ag-input" type="number" min="1" value={dailyCap} onChange={e => setDailyCap(e.target.value)} />
            </label>
          </div>

          <div className="ag-fsec">Notifications</div>
          <label className="ag-field">
            <span className="ag-field__label">Notify me</span>
            <AgentSelect value={notifyMode} onChange={setNotifyMode} options={[
              { value: "DAILY_DIGEST", label: "Daily digest — one summary a day" },
              { value: "ON_FAILURE", label: "Only when a run fails" },
              { value: "OFF", label: "Off — don't email me" }
            ]} />
          </label>
        </div>

        <div className="ag-rail">
          <div className="ag-summary">
            <h4>⚡ What this agent does</h4>
            <ul className="ag-slist">
              {step(<span>Sources contacts from <b>{sourceLabel}</b>, {runText}</span>)}
              {step(<span>Skips anyone already processed or on DNC / undeliverable</span>)}
              {step(<span>Finds &amp; verifies emails (for email sequences)</span>)}
              {sequenceId && step(<span>Enrolls into <b>{sequenceName}</b></span>)}
              {notifyMode !== "OFF" && step(<span>{notifyMode === "DAILY_DIGEST" ? "Emails you a daily digest" : "Emails you when a run fails"}</span>)}
            </ul>
          </div>
          <button className="ag-btn ag-btn--primary" style={{ justifyContent: "center" }} disabled={!canSubmit || saving} onClick={submit}>
            {saving ? "Creating…" : "Create agent →"}
          </button>
          <button className="ag-btn" style={{ justifyContent: "center" }} disabled={saving} onClick={customize}>Customize steps →</button>
          <button className="ag-btn" style={{ justifyContent: "center" }} onClick={() => history.push("/agents")}>Cancel</button>
          {!canSubmit && <p className="ag-hint" style={{ textAlign: "center" }}>Pick a source and a sequence to create — or “Customize steps” to edit the flow first.</p>}
        </div>
      </div>

      <SequencePickerModal
        open={seqModalOpen}
        currentId={sequenceId}
        onSelect={s => { setSequenceId(s.id); setSequenceName(s.name); }}
        onClose={() => setSeqModalOpen(false)}
      />
    </div>
  );
});

export default CreateListProspector;
